import React from "react";
import { CHART_COLORS } from "@/features/investors/components/charts/palette";

export type StackedSeries = {
    label: string;
    color: string;
    values: number[];
};

type Props = {
    series: StackedSeries[];
    labels: string[];
    height?: number;
    formatValue: (value: number) => string;
};

export const StackedBars: React.FC<Props> = ({ series, labels, height = 220, formatValue }) => {
    if (labels.length === 0 || series.length === 0) return null;

    const W = 640;
    const H = height;
    const P = { t: 16, r: 14, b: 28, l: 14 };
    const iw = W - P.l - P.r;
    const ih = H - P.t - P.b;
    const totals = labels.map((_, index) =>
        series.reduce((total, entry) => total + entry.values[index], 0)
    );
    const max = Math.max(...totals) * 1.12 || 1;
    const gap = iw / labels.length;
    const barWidth = gap * 0.5;
    const gridLines = [0, 1, 2, 3].map((step) => P.t + (ih / 3) * step);

    return (
        <svg
            viewBox={`0 0 ${W} ${H}`}
            width="100%"
            preserveAspectRatio="xMidYMid meet"
            style={{ display: "block" }}
        >
            {gridLines.map((gy) => (
                <line
                    key={gy}
                    x1={P.l}
                    y1={gy.toFixed(1)}
                    x2={W - P.r}
                    y2={gy.toFixed(1)}
                    stroke={CHART_COLORS.grid}
                    strokeWidth={1}
                />
            ))}
            {labels.map((label, index) => {
                const bx = P.l + gap * index + (gap - barWidth) / 2;
                let accumulated = 0;
                return (
                    <g key={label}>
                        {series.map((entry) => {
                            const value = entry.values[index];
                            const barHeight = (value / max) * ih;
                            accumulated += value;
                            const by = P.t + ih - (accumulated / max) * ih;
                            return (
                                <rect
                                    key={entry.label}
                                    x={bx.toFixed(1)}
                                    y={by.toFixed(1)}
                                    width={barWidth.toFixed(1)}
                                    height={Math.max(barHeight, 0.5).toFixed(1)}
                                    fill={entry.color}
                                    opacity={0.92}
                                    data-tip={`<b>${label} · ${entry.label}</b><br>${formatValue(value)}`}
                                    style={{ cursor: "pointer" }}
                                />
                            );
                        })}
                    </g>
                );
            })}
            {labels.map((label, index) => (
                <text
                    key={label}
                    x={(P.l + gap * index + gap / 2).toFixed(1)}
                    y={H - 8}
                    fill={CHART_COLORS.axis}
                    fontSize={11}
                    fontWeight={500}
                    textAnchor="middle"
                >
                    {label}
                </text>
            ))}
        </svg>
    );
};
